// src/pages/photobook/PhotoBook.jsx
import { useEffect, useState } from "react";
import CollectionPage from "../Collectionpage";
import { apiFetch } from "../../api/api";
import SEOHead from "../../components/common/SEOHead";
import ProductListSchema from "../../components/common/ProductListSchema";
import FAQSchema from "../../components/common/FAQSchema";

const PHOTOBOOK_CATEGORY_SLUG = "photobook";
const PRODUCTS_PER_PAGE = 12;
const PAGE_URL = "https://aishcraft.com/photobook";

function mapProduct(r) {
  return {
    ...r,
    featured: !!r.isFeatured,
    date: r.createdAt || null,
  };
}

const config = {
  productsPerPage: PRODUCTS_PER_PAGE,
  basePath: "/photobook",
  announcement: {
    headline: "Capture your memories in a beautiful photobook!",
    subtext: "Customise every page • Ships in 6 working days",
  },
  hero: {
    title: "Photobooks",
    subtitle: "Your memories, beautifully bound",
    featuredIn: ["PREMIUM QUALITY", "TRUSTED CRAFTSMANSHIP", "PAN INDIA DELIVERY"],
    quote: "The quality blew me away. Every photo looked stunning — exactly what I imagined.",
    quoteLink: "#",
    rating: "4.8",
    ratingCount: "1000+",
  },
};

const photobookFAQs = [
  {
    question: "How long does photobook delivery take?",
    answer: "Personalized photobooks are made to order and typically ship within 6 working days across India.",
  },
  {
    question: "Can I add my own photos to the photobook?",
    answer: "Yes, you can upload and arrange your own photos, add custom text, and choose from multiple cover designs.",
  },
  {
    question: "What paper and cover options are available?",
    answer: "We offer premium matte and glossy paper with both hardcover and softcover options.",
  },
];

export default function PhotoBook() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(`/public/products.php?category=${PHOTOBOOK_CATEGORY_SLUG}`);
        if (!cancelled) setProducts((res.data || []).map(mapProduct));
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load photobooks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const seo = (
    <SEOHead
      title="Custom Photobooks"
      description="Order personalized photobooks made from your memories. Premium quality prints, custom covers, pan India delivery."
      url={PAGE_URL}
      breadcrumbs={[
        { name: "Home", item: "https://aishcraft.com/" },
        { name: "Photobooks", item: PAGE_URL },
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-black animate-spin" />
            <p className="text-sm text-gray-500">Loading photobooks...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center px-4 text-center">
          <div>
            <p className="text-sm text-red-500 font-semibold mb-3">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-semibold hover:opacity-90"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {seo}
      <ProductListSchema products={products} pageUrl={PAGE_URL} />
      <FAQSchema faqs={photobookFAQs} />
      <CollectionPage config={{ ...config, products }} />
    </>
  );
}